import type { ReactElement } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { useCcConfig, useSettingsModal } from '../../store';
import { TabPills } from '../tab-pills';
import { QuickSwitchCard } from './card';
import type { QuickSwitchEvent } from './event';
import { useQuickSwitchLiveEvents } from './use-live-events';
import { useQuickSwitchMyEvents } from './use-my-events';

const Root = styled.div`
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0px 4px 8px;

  .label {
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.45);
    font-weight: 700;
    font-size: 9px;
  }

  .rule {
    flex: 1 1 0%;
    height: 1px;
    background: rgba(255, 255, 255, 0.07);
  }
`;

const PillsRow = styled.div`
  margin-bottom: 8px;

  /* Full-width pill group, splitting the row evenly between the tabs. */
  > nav {
    display: flex;
    width: 100%;
  }

  button {
    flex: 1 1 0%;
    justify-content: center;
  }
`;

const List = styled.div`
  flex: 1 1 0%;
  display: flex;
  flex-direction: column;
  gap: 5px;
  overflow: hidden auto;
  min-height: 0px;
  padding-bottom: 6px;
`;

const Empty = styled.div`
  padding: 10px 4px;
  color: rgba(176, 186, 209, 0.45);
  font-size: 11px;
`;

const SettingsLink = styled.button`
  display: block;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: none;
  color: rgb(159, 176, 255);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const Heading = (): ReactElement => (
  <SectionHeader>
    <span className='label'>Quick Switch</span>
    <span className='rule' />
  </SectionHeader>
);

type QuickSwitchTab = 'live' | 'myEvents';

/*
 * The badge says how many there are, not how many fit. It follows the list once that is the larger of
 * the two: the live feed keeps itself current from the socket, so it can grow past the fetched total.
 */
const feedCount = ({ events, total }: { events: QuickSwitchEvent[]; total?: number }): number =>
  Math.max(events.length, total ?? 0);

const FeedList = ({
  events,
  loading,
  empty,
  total
}: {
  events: QuickSwitchEvent[];
  loading: boolean;
  empty: string;
  /* How many there are in all, when the feed knows; the list itself is capped. */
  total?: number;
}): ReactElement => {
  if (loading) {
    return <Empty>Loading events…</Empty>;
  }

  if (!events.length) {
    return <Empty>{empty}</Empty>;
  }

  const hidden = total === undefined ? 0 : total - events.length;

  return (
    <>
      {events.map((event) => (
        <QuickSwitchCard key={event.eid} event={event} />
      ))}
      {hidden > 0 && <Empty>and {hidden} more — open the events list to see them all.</Empty>}
    </>
  );
};

interface Props {
  readonly expanded: boolean;
}

const Feeds = (): ReactElement => {
  const [tab, setTab] = useState<QuickSwitchTab>('live');
  const live = useQuickSwitchLiveEvents();
  const mine = useQuickSwitchMyEvents();

  return (
    <Root>
      <Heading />

      <PillsRow>
        <TabPills
          small
          darker
          pills={[
            {
              key: 'live',
              label: 'Live',
              /* No badge until there is a real number: a 0 while loading reads as an answer. */
              count: live.loading ? undefined : feedCount(live),
              active: tab === 'live',
              onClick: (): void => {
                setTab('live');
              }
            },
            {
              key: 'myEvents',
              label: 'My Events',
              count: mine.loading ? undefined : feedCount(mine),
              active: tab === 'myEvents',
              onClick: (): void => {
                setTab('myEvents');
              }
            }
          ]}
        />
      </PillsRow>

      <List className='cc-scroll'>
        {tab === 'live' && (
          <FeedList
            events={live.events}
            loading={live.loading}
            total={live.total}
            empty='No live events right now.'
          />
        )}

        {tab === 'myEvents' && (
          <FeedList
            events={mine.events}
            loading={mine.loading}
            total={mine.total}
            empty='No events assigned to you.'
          />
        )}
      </List>
    </Root>
  );
};

const FeedsOff = (): ReactElement => {
  const openSettings = useSettingsModal((v) => v.setOpen);

  return (
    <Root>
      <Heading />
      <Empty>
        Event feeds are off.
        <SettingsLink
          type='button'
          onClick={(): void => {
            openSettings(true);
          }}
        >
          Turn on in Settings
        </SettingsLink>
      </Empty>
    </Root>
  );
};

export const QuickSwitch = ({ expanded }: Props): ReactElement | null => {
  const showFeeds = useCcConfig((v) => v.showSidebarFeeds);

  if (!expanded) {
    return null;
  }

  /* Off means not mounted, not hidden: hidden feeds would keep their socket subscriptions and requests. */
  return showFeeds ? <Feeds /> : <FeedsOff />;
};
